import { configureStore } from '@reduxjs/toolkit';
import { api } from '@/services/ApiService.ts';

const reducer = {
    // Add the generated reducer as a specific top-level slice
    [api.reducerPath]: api.reducer,
};

/** Shape `preloadedState` must have: whatever the api slice holds, and nothing else. */
export type PreloadedAppState = {
    [K in typeof api.reducerPath]: ReturnType<typeof api.reducer>;
};

/**
 * `preloadedState` is optional initial state. A hydrating client does not use it: it seeds only the
 * feature flags the server rendered with (App.tsx), not the server's whole RTK state.
 */
export const setupStore = (preloadedState?: PreloadedAppState) => configureStore({
    preloadedState,
    reducer,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware().concat(api.middleware),
});

export type AppStore = ReturnType<typeof setupStore>;

/**
 * Puts the feature flags a server render used into the RTK Query cache as a fulfilled
 * `getFeatureFlags` result. `upsertQueryEntries` is a plain synchronous action, so the entry is
 * there before the first render; an async upsert would leave that render on a pending query and
 * the page would not match the server's HTML.
 */
export function seedFeatureFlags(store: AppStore, flags: Record<string, boolean>): void {
    store.dispatch(api.util.upsertQueryEntries([{ endpointName: 'getFeatureFlags', arg: undefined, value: flags }]));
}
export type AppDispatch = AppStore['dispatch'];
